/**
 * Assembly + design-group suppress: normalize setup_fee / setup_fees_details for
 * place order and for syncing cart DB after line-item deletes.
 */
import {
    applySuppressionToSetupFeeDetails,
    expandSetupFeeDetailsToMatchTargetTotal,
    getRawSetupFeesDetailsTotal,
    parseSetupFeesDetailsForTotal
} from "@/common/itemSuppressDisplayShared";

function parseDecoration(cartLine) {
    if (!cartLine || !cartLine.decoration) return null;
    let d = cartLine.decoration;
    if (typeof d === "string") {
        try {
            d = JSON.parse(d);
        } catch (e) {
            return null;
        }
    }
    return d;
}

export function isSuppressDesignGroupingEnabledForCartLine(cartLine) {
    const d = parseDecoration(cartLine);
    if (!d) return false;
    const pms = d.product_master_service || {};
    const ms = d.master_service || {};
    return (
        Number(
            pms.suppress_additional_setup_run_charge ||
                ms.suppress_additional_setup_run_charge ||
                0
        ) === 1
    );
}

/**
 * Full-catalog setup_fees_details for a mixed (suppress + non-suppress) assembly group.
 * Used by cart display and place-order normalization (merge order must not matter).
 */
export function resolveMixedAssemblySetupDetailsFromGroup(groupItems) {
    if (!Array.isArray(groupItems) || groupItems.length < 2) {
        return null;
    }
    const anyNonSuppress = groupItems.some(
        (line) => !isSuppressDesignGroupingEnabledForCartLine(line)
    );
    if (!anyNonSuppress) {
        return null;
    }

    let maxSignal = 0;
    groupItems.forEach((line) => {
        if (!line) return;
        const d = parseSetupFeesDetailsForTotal(line.setup_fees_details);
        const raw = d ? getRawSetupFeesDetailsTotal(d) : 0;
        const col = parseFloat(line.setup_fee || 0) || 0;
        maxSignal = Math.max(maxSignal, raw, col);
    });
    if (maxSignal <= 0) {
        return null;
    }

    let bestDetails = null;
    let bestScore = -1;
    groupItems.forEach((line) => {
        if (!line) return;
        const d = parseSetupFeesDetailsForTotal(line.setup_fees_details);
        if (!d || !Array.isArray(d.locations)) return;
        const raw = getRawSetupFeesDetailsTotal(d);
        const col = parseFloat(line.setup_fee || 0) || 0;
        const score = Math.max(raw, col);
        if (score > bestScore) {
            bestScore = score;
            bestDetails = d;
        }
    });

    const fallbackItem = groupItems[0];
    const base =
        bestDetails ||
        (fallbackItem &&
            parseSetupFeesDetailsForTotal(fallbackItem.setup_fees_details));
    if (!base || !Array.isArray(base.locations)) {
        return null;
    }

    let out = JSON.parse(JSON.stringify(base));
    const rawOut = getRawSetupFeesDetailsTotal(out);
    if (maxSignal > rawOut + 0.021) {
        const expanded = expandSetupFeeDetailsToMatchTargetTotal(
            out,
            maxSignal
        );
        if (expanded) {
            out = expanded;
        }
    }
    return out;
}

/**
 * Assembly row that mixes suppress + non-suppress products must show full catalog setup on the
 * header (e.g. $110 for two DT locations), not design-group–suppressed rows ($55 + $0).
 */
export function getAssemblyMixedCaseDisplaySetupDetails(item, groupedItems) {
    if (!item || !Array.isArray(groupedItems) || groupedItems.length < 2) {
        return null;
    }
    if (groupedItems[0] !== item) {
        return null;
    }
    return resolveMixedAssemblySetupDetailsFromGroup(groupedItems);
}

function cloneDetails(val) {
    if (val == null || val === "") return null;
    try {
        const o =
            typeof val === "string"
                ? JSON.parse(val)
                : JSON.parse(JSON.stringify(val));
        return o;
    } catch (e) {
        return null;
    }
}

function stringifyDetails(d) {
    if (d == null || d === "") return null;
    return typeof d === "string" ? d : JSON.stringify(d);
}

function normalizeDetailsCompare(d) {
    if (d == null || d === "") return "";
    try {
        return typeof d === "string" ? d : JSON.stringify(d);
    } catch (e) {
        return "";
    }
}

/** Lone assembly row after sibling removed; suppress product should bill its own setup only. */
export function isAssemblySuppressOrphanLine(line) {
    if (!line) return false;
    const gid = line.group_assembly_id;
    if (
        !gid ||
        gid === "null" ||
        gid === "undefined" ||
        (line.subItems && line.subItems.length > 0)
    ) {
        return false;
    }
    return isSuppressDesignGroupingEnabledForCartLine(line);
}

/**
 * Single merged row, no subItems, still has group_assembly_id (orphan after sibling removed).
 * Re-apply imprint-based suppression to stored setup_fees_details so upgraded catalog totals drop back.
 */
export function applyAssemblyOrphanSuppressRevert(line) {
    if (!isAssemblySuppressOrphanLine(line)) return;
    const parsed = parseSetupFeesDetailsForTotal(line.setup_fees_details);
    if (!parsed || !Array.isArray(parsed.locations)) return;
    const suppressed = applySuppressionToSetupFeeDetails(
        cloneDetails(line.setup_fees_details),
        line,
        null,
        null
    );
    if (!suppressed) return;
    const t = parseFloat(suppressed.total || 0);
    line.setup_fee = t;
    line.setup_fees_details = stringifyDetails(suppressed);
}

/**
 * Mutates one assembly parent row (with subItems) in place.
 */
export function applyAssemblyPlaceOrderSetupToItem(parentRow) {
    if (!parentRow || !parentRow.subItems || parentRow.subItems.length === 0) {
        return;
    }
    const gid = parentRow.group_assembly_id;
    if (!gid || gid === "null" || gid === "undefined") return;

    const group = [parentRow, ...parentRow.subItems];
    const anyNonSuppress = group.some(
        (line) => !isSuppressDesignGroupingEnabledForCartLine(line)
    );

    const parentCandidate =
        group.find((line) => parseInt(line.assembly_parent, 10) === 1) ||
        parentRow;

    if (anyNonSuppress) {
        let maxCatalog = 0;
        let winner = parentCandidate;
        group.forEach((line) => {
            if (!line) return;
            const raw = getRawSetupFeesDetailsTotal(line.setup_fees_details);
            const col = parseFloat(line.setup_fee || 0) || 0;
            const candidate = Math.max(raw, col);
            if (candidate > maxCatalog) {
                maxCatalog = candidate;
                winner = line;
            }
        });

        // Cart UI can show upgraded setup_fee (e.g. 110) while setup_fees_details JSON is still
        // design-group suppressed (55 + back $0). Place order must send details that match feeNum.
        let details = resolveMixedAssemblySetupDetailsFromGroup(group);

        if (!details || !Array.isArray(details.locations)) {
            details = cloneDetails(winner.setup_fees_details);
            if (!details || !Array.isArray(details.locations)) {
                details = cloneDetails(parentCandidate.setup_fees_details);
            }
        }

        const feeNum = maxCatalog > 0 ? maxCatalog : 0;
        if (
            details &&
            Array.isArray(details.locations) &&
            feeNum > getRawSetupFeesDetailsTotal(details) + 0.021
        ) {
            const expanded = expandSetupFeeDetailsToMatchTargetTotal(
                details,
                feeNum
            );
            if (expanded) {
                details = expanded;
            }
        }

        parentCandidate.setup_fee = feeNum;
        parentCandidate.setup_fees_details = stringifyDetails(details);
        group.forEach((line) => {
            if (String(line.id) === String(parentCandidate.id)) return;
            line.setup_fee = 0;
            line.setup_fees_details = null;
        });
        return;
    }

    let bestDetails = cloneDetails(parentCandidate.setup_fees_details);
    let maxRaw = getRawSetupFeesDetailsTotal(bestDetails);
    if (!bestDetails || !Array.isArray(bestDetails.locations)) {
        maxRaw = 0;
        bestDetails = null;
    }
    group.forEach((line) => {
        const cand = cloneDetails(line.setup_fees_details);
        if (!cand || !Array.isArray(cand.locations)) return;
        const r = getRawSetupFeesDetailsTotal(cand);
        if (!bestDetails || r > maxRaw) {
            maxRaw = r;
            bestDetails = cand;
        }
    });
    if (!bestDetails || !Array.isArray(bestDetails.locations)) return;

    const suppressed = applySuppressionToSetupFeeDetails(
        JSON.parse(JSON.stringify(bestDetails)),
        parentCandidate,
        null,
        null
    );
    if (!suppressed) return;
    parentCandidate.setup_fee = parseFloat(suppressed.total || 0);
    parentCandidate.setup_fees_details = stringifyDetails(suppressed);
    group.forEach((line) => {
        if (String(line.id) === String(parentCandidate.id)) return;
        line.setup_fee = 0;
        line.setup_fees_details = null;
    });
}

/**
 * In-place normalize for merged cart items (parent + subItems).
 */
export function prepareCartItemsAssemblyPlaceOrderInPlace(cartItems) {
    if (!Array.isArray(cartItems)) return cartItems;
    cartItems.forEach((item) => {
        if (item.subItems && item.subItems.length > 0) {
            applyAssemblyPlaceOrderSetupToItem(item);
        } else {
            applyAssemblyOrphanSuppressRevert(item);
        }
    });
    return cartItems;
}

function findLineByIdInMerged(mergedItems, id) {
    for (const p of mergedItems) {
        if (String(p.id) === String(id)) return p;
        for (const s of p.subItems || []) {
            if (String(s.id) === String(id)) return s;
        }
    }
    return null;
}

function collectAllMergedLines(mergedItems, out) {
    mergedItems.forEach((p) => {
        if (p.group_assembly_id) out.push(p);
        (p.subItems || []).forEach((s) => {
            if (s.group_assembly_id) out.push(s);
        });
    });
}

/**
 * Returns API patches needed so DB matches normalized assembly rules.
 */
export function getAssemblySetupPatchesFromMergedCart(mergedItems) {
    if (!Array.isArray(mergedItems) || mergedItems.length === 0) return [];
    let cloned;
    try {
        cloned = JSON.parse(JSON.stringify(mergedItems));
    } catch (e) {
        return [];
    }
    prepareCartItemsAssemblyPlaceOrderInPlace(cloned);

    const desired = [];
    collectAllMergedLines(cloned, desired);

    const patches = [];
    const seen = new Set();
    desired.forEach((want) => {
        const id = want.id;
        if (id == null || seen.has(id)) return;
        const orig = findLineByIdInMerged(mergedItems, id);
        if (!orig) return;
        const wFee = parseFloat(want.setup_fee || 0) || 0;
        const oFee = parseFloat(orig.setup_fee || 0) || 0;
        const wDet = normalizeDetailsCompare(want.setup_fees_details);
        const oDet = normalizeDetailsCompare(orig.setup_fees_details);
        if (Math.abs(wFee - oFee) > 0.02 || wDet !== oDet) {
            patches.push({
                cart_id: id,
                setup_fee: wFee,
                setup_fees_details: want.setup_fees_details
            });
            seen.add(id);
        }
    });
    return patches;
}
